"use client"

import { useEffect, useState } from "react"

interface OverlayProps {
  targetId: string | null
  onClick?: () => void
}

export function SpotlightOverlay({ targetId, onClick }: OverlayProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (!targetId) return

    const updateRect = () => {
      const element = document.getElementById(targetId)
      if (element) {
        setTargetRect(element.getBoundingClientRect())
      }
    }

    let attempts = 0
    const maxAttempts = 50
    let animationFrameId: number

    const waitForElement = () => {
      const element = document.getElementById(targetId)
      if (element) {
        updateRect()
      } else if (attempts < maxAttempts) {
        attempts++
        animationFrameId = requestAnimationFrame(waitForElement)
      }
    }

    waitForElement()

    window.addEventListener("resize", updateRect)
    window.addEventListener("scroll", updateRect)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener("resize", updateRect)
      window.removeEventListener("scroll", updateRect)
    }
  }, [targetId])

  if (!targetRect) return null

  const padding = 8

  return (
    <div className="fixed inset-0 z-40" onClick={onClick} style={{ pointerEvents: onClick ? "auto" : "none" }}>
      <svg width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0 }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={targetRect.left - padding}
              y={targetRect.top - padding}
              width={targetRect.width + padding * 2}
              height={targetRect.height + padding * 2}
              rx="12"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.7)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: "none" }}
        />
      </svg>
    </div>
  )
}

export function HighlightOverlay({ targetId, onClick }: OverlayProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (!targetId) return

    const updateRect = () => {
      const element = document.getElementById(targetId)
      if (element) {
        setTargetRect(element.getBoundingClientRect())
      }
    }

    let attempts = 0
    const maxAttempts = 50
    let animationFrameId: number

    const waitForElement = () => {
      const element = document.getElementById(targetId)
      if (element) {
        updateRect()
      } else if (attempts < maxAttempts) {
        attempts++
        animationFrameId = requestAnimationFrame(waitForElement)
      }
    }

    waitForElement()

    window.addEventListener("resize", updateRect)
    window.addEventListener("scroll", updateRect)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener("resize", updateRect)
      window.removeEventListener("scroll", updateRect)
    }
  }, [targetId])

  if (!targetRect) return null

  const padding = 8

  return (
    <div
      className="fixed z-40 rounded-xl border-2 border-blue-500 shadow-lg animate-pulse"
      style={{
        top: `${targetRect.top - padding}px`,
        left: `${targetRect.left - padding}px`,
        width: `${targetRect.width + padding * 2}px`,
        height: `${targetRect.height + padding * 2}px`,
        cursor: onClick ? "pointer" : "none",
      }}
      onClick={onClick}
    />
  )
}
